const ENABLED_MIN_WIDTH = 700;
const MAX_ROCKETS = 5;
const MAX_PARTICLES = 260;
const AUTO_LAUNCH_EVERY_MS = 1150;

const palette = [
  '#f6c343',
  '#ffe08a',
  '#c92424',
  '#ff4d3d',
  '#ffefb8',
  '#ffffff',
];

class HolidayFireworks {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'holiday-304-fireworks';
    this.canvas.setAttribute('aria-hidden', 'true');
    this.context = this.canvas.getContext('2d');
    this.rockets = [];
    this.particles = [];
    this.lastTime = 0;
    this.lastAutoLaunch = 0;
    this.running = false;

    this.resize = this.resize.bind(this);
    this.frame = this.frame.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);

    this.start();
  }

  get enabled() {
    return window.innerWidth >= ENABLED_MIN_WIDTH && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  start() {
    if (!this.enabled || this.running) {
      return;
    }

    document.body.append(this.canvas);
    this.resize();
    this.running = true;
    this.lastTime = performance.now();
    this.lastAutoLaunch = this.lastTime;

    window.addEventListener('resize', this.resize, { passive: true });
    document.addEventListener('pointerdown', this.handlePointerDown, true);
    document.addEventListener('visibilitychange', this.handleVisibility);
    requestAnimationFrame(this.frame);
  }

  stop() {
    this.running = false;
    this.rockets = [];
    this.particles = [];
    this.canvas.remove();
    window.removeEventListener('resize', this.resize);
    document.removeEventListener('pointerdown', this.handlePointerDown, true);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  resize() {
    if (!this.enabled) {
      this.stop();
      return;
    }

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.canvas.width = Math.round(this.width * ratio);
    this.canvas.height = Math.round(this.height * ratio);
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  frame(now) {
    if (!this.running) {
      return;
    }

    if (document.hidden) {
      requestAnimationFrame(this.frame);
      return;
    }

    const delta = Math.min(now - this.lastTime, 42);
    this.lastTime = now;

    if (now - this.lastAutoLaunch > AUTO_LAUNCH_EVERY_MS && this.rockets.length < MAX_ROCKETS) {
      this.launchRocket();
      this.lastAutoLaunch = now;
    }

    this.update(delta);
    this.draw();
    requestAnimationFrame(this.frame);
  }

  launchRocket(targetX, targetY) {
    const x = targetX ?? this.width * (.16 + Math.random() * .68);
    const y = targetY ?? this.height * (.14 + Math.random() * .42);
    const startX = x + (Math.random() - .5) * 100;

    this.rockets.push({
      x: startX,
      y: this.height + 20,
      targetX: x,
      targetY: y,
      vx: (x - startX) * .0018,
      vy: -(.48 + Math.random() * .18),
      color: this.pickColor(),
      trail: [],
    });
  }

  update(delta) {
    for (const rocket of this.rockets) {
      rocket.trail.push({ x: rocket.x, y: rocket.y, age: 0 });

      if (rocket.trail.length > 10) {
        rocket.trail.shift();
      }

      for (const point of rocket.trail) {
        point.age += delta;
      }

      rocket.x += rocket.vx * delta;
      rocket.y += rocket.vy * delta;
      rocket.vy += .00036 * delta;

      if (rocket.y <= rocket.targetY || rocket.vy >= -.04) {
        rocket.done = true;
        this.explode(rocket.x, rocket.y, rocket.color);
      }
    }

    this.rockets = this.rockets.filter((rocket) => !rocket.done);

    for (const particle of this.particles) {
      particle.age += delta;
      particle.x += particle.vx * delta;
      particle.y += particle.vy * delta;
      particle.vy += particle.gravity * delta;
      particle.vx *= .992;
      particle.vy *= .992;
    }

    this.particles = this.particles
      .filter((particle) => particle.age < particle.life)
      .slice(-MAX_PARTICLES);
  }

  explode(x, y, baseColor) {
    const count = 52 + Math.floor(Math.random() * 22);
    const ringOffset = Math.random() * Math.PI;

    for (let index = 0; index < count; index += 1) {
      const angle = ringOffset + (Math.PI * 2 * index) / count;
      const speed = .08 + Math.random() * .2;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        gravity: .00016 + Math.random() * .00008,
        age: 0,
        life: 860 + Math.random() * 420,
        color: Math.random() > .34 ? baseColor : this.pickColor(),
        size: 2.2 + Math.random() * 3.6,
      });
    }
  }

  draw() {
    this.context.clearRect(0, 0, this.width, this.height);
    this.context.save();
    this.context.globalCompositeOperation = 'lighter';

    for (const rocket of this.rockets) {
      rocket.trail.forEach((point, index) => {
        const alpha = (index + 1) / rocket.trail.length * .34;
        this.context.fillStyle = this.withAlpha(rocket.color, alpha);
        this.context.fillRect(Math.round(point.x), Math.round(point.y), 2, 8);
      });

      this.context.fillStyle = rocket.color;
      this.context.fillRect(Math.round(rocket.x) - 1, Math.round(rocket.y) - 4, 3, 8);
    }

    for (const particle of this.particles) {
      const progress = particle.age / particle.life;
      const alpha = Math.max(0, 1 - progress);
      const size = Math.max(1, particle.size * (1 - progress * .5));

      this.context.fillStyle = this.withAlpha(particle.color, alpha);
      this.context.fillRect(Math.round(particle.x), Math.round(particle.y), size, size);
      this.context.fillRect(Math.round(particle.x - size), Math.round(particle.y), size * .5, size * .5);
      this.context.fillRect(Math.round(particle.x + size), Math.round(particle.y), size * .5, size * .5);
    }

    this.context.restore();
  }

  handlePointerDown(event) {
    if (!this.running || event.pointerType === 'touch') {
      return;
    }

    const target = event.target;

    if (target.closest && target.closest('a, button, input, textarea, select, summary, label, [role="button"]')) {
      return;
    }

    this.explode(event.clientX, event.clientY, this.pickColor());
  }

  handleVisibility() {
    this.lastTime = performance.now();
  }

  pickColor() {
    return palette[Math.floor(Math.random() * palette.length)];
  }

  withAlpha(color, alpha) {
    const value = color.replace('#', '');
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);

    return `rgb(${r} ${g} ${b} / ${alpha})`;
  }
}

new HolidayFireworks();
